import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createCheckoutSession, fetchPlans } from "../store/plans/plansSlice";
import { fetchCurrentSubscription } from "../store/billing/billingSlice";

function formatPlanPrice(amountCents) {
  const cents = Number(amountCents ?? 0);
  if (Number.isNaN(cents)) return "£0";
  const amount = cents / 100;
  if (amount === 0) return "Free";
  // Display as GBP by default (matches current UI using £).
  return `£${amount.toFixed(2).replace(/\\.00$/, "")}`;
}

function isCurrentPlan(subscriptionPlan, plan) {
  if (!subscriptionPlan) return false;
  const sp = String(subscriptionPlan).toLowerCase();

  const candidates = [plan?.id, plan?.name, plan?.plan_title]
    .filter((v) => v !== undefined && v !== null)
    .map((v) => String(v).toLowerCase());

  return candidates.some((c) => c === sp || c.includes(sp) || sp.includes(c));
}

function Subscription({ onOpenAuth, onOpenMenu }) {
  const dispatch = useAppDispatch();
  const plans = useAppSelector((s) => s.plans.items);
  const status = useAppSelector((s) => s.plans.status);
  const error = useAppSelector((s) => s.plans.error);
  const checkoutStatus = useAppSelector((s) => s.plans.checkoutStatus);
  const checkoutError = useAppSelector((s) => s.plans.checkoutError);
  const token = useAppSelector((s) => s.auth.token);
  const subscriptionState = useAppSelector((s) => s.billing?.subscription);
  const billingStatus = useAppSelector((s) => s.billing?.status);
  const billingError = useAppSelector((s) => s.billing?.error);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (status === "idle") dispatch(fetchPlans());
  }, [status, dispatch]);

  useEffect(() => {
    if (token && billingStatus === "idle") {
      dispatch(fetchCurrentSubscription());
    }
  }, [token, billingStatus, dispatch]);

  async function handleGetStarted(planId) {
    if (!token) {
      onOpenAuth?.("login");
      return;
    }
    setSelectedPlanId(planId);
    try {
      const session = await dispatch(createCheckoutSession(planId)).unwrap();
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      // eslint-disable-next-line no-alert
      alert("Checkout session created, but no URL was returned.");
    } catch (e) {
      // Error message comes from redux `checkoutError` below.
    } finally {
      setSelectedPlanId(null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 md:px-10">
        <section className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900 md:text-3xl">
            Choose your favorite Package
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Select one of your favorite package and get the facilities
          </p>
        </section>

        {token && (
          <section className="mt-6">
            {billingStatus === "loading" ? (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
                Loading current subscription...
              </div>
            ) : billingError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 whitespace-pre-line">
                {billingError}
              </div>
            ) : subscriptionState ? (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-900">
                  Current Plan: {subscriptionState.plan || "—"}
                </div>
                <div className="mt-1">Status: {subscriptionState.status || "—"}</div>
                <div className="mt-1">
                  Current period end:{" "}
                  {subscriptionState.current_period_end
                    ? new Date(subscriptionState.current_period_end).toLocaleDateString()
                    : "—"}
                </div>
                {subscriptionState.cancel_at_period_end && (
                  <div className="mt-1 text-emerald-700">
                    Cancelling at period end.
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
                No active subscription.
              </div>
            )}
          </section>
        )}

        {status === "loading" ? (
          <section className="mt-8 flex items-center justify-center">
            <span className="text-sm text-slate-500">Loading plans...</span>
          </section>
        ) : error ? (
          <section className="mt-8">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
              {error}
            </div>
          </section>
        ) : plans.length === 0 ? (
          <section className="mt-8">
            <div className="text-sm text-slate-500">No active plans available.</div>
          </section>
        ) : (
          <section className="mt-8 grid gap-6 md:grid-cols-3">
            {plans.map((plan) => {
              const priceLabel = formatPlanPrice(plan.amount_cents);
              const intervalLabel = plan.interval === "day" ? "per day" : "per month";
              const descriptionText =
                typeof plan.description === "string" && plan.description.trim()
                  ? plan.description.trim()
                  : plan.plan_title || "";
              const isCurrent = isCurrentPlan(subscriptionState?.plan, plan);

              return (
                <article
                  key={plan.id}
                  className="flex flex-col justify-between rounded-2xl border border-slate-200 bg-white px-6 py-6 text-left shadow-sm"
                >
                  <div>
                    <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                      {plan.name || plan.plan_title || "Plan"}
                    </h2>
                    {isCurrent && (
                      <p className="mt-2 text-xs font-semibold text-emerald-600">
                        Current Plan
                      </p>
                    )}
                    <p className="mt-3 text-2xl font-semibold text-slate-900">
                      {priceLabel}
                      <span className="ml-2 text-sm font-normal text-slate-500">
                        {plan.amount_cents ? `/${intervalLabel}` : ""}
                      </span>
                    </p>

                    {!!descriptionText && (
                      <p className="mt-4 text-xs text-slate-600 leading-relaxed">
                        {descriptionText}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    className="mt-6 w-full rounded-md bg-[#4F46E5] px-4 py-2 text-xs font-semibold text-white hover:bg-[#4F46E5]"
                    onClick={() => handleGetStarted(plan.id)}
                    disabled={
                      isCurrent ||
                      (checkoutStatus === "loading" &&
                        selectedPlanId === plan.id)
                    }
                  >
                    {isCurrent
                      ? "Current Plan"
                      : checkoutStatus === "loading" &&
                        selectedPlanId === plan.id
                      ? "Redirecting..."
                      : "Get Started →"}
                  </button>
                </article>
              );
            })}
          </section>
        )}

        {checkoutError && (
          <section className="mt-6 px-4">
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
              {checkoutError}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default Subscription;

